import React, { useRef } from 'react';
/**
 * DjayAvatar — render primitive (orchestrator).
 *
 * Split from the former ~1619-line `DjayAvatar.tsx` god-file into a scalable
 * `mascot/avatar/` module (MASCOT_SSOT_ARCHITECTURE_AUDIT_2026 §4.1). This
 * orchestrator owns ONLY: size→detail-flag derivation, the unique gradient
 * `uid`, the outer <svg> shell + body gradient <defs>, and the assembly of the
 * body layers + the always-rendered floating accessory. All per-feature SVG
 * lives in `./parts/*`.
 *
 * This stays the canonical render primitive: product code must use the intent
 * wrappers `<DjayIdentity>` / `<DjaySpecimen>` / `<DjayNarrator>` instead of
 * rendering `<DjayAvatar>` directly (lint-guarded, see .eslintrc.json §6/§9.6).
 *
 * Import note (audit ronde 10): only `useRef` is needed from React — JSX is
 * transformed by the bundler. Adding unused React imports has regressed twice.
 */
import { SIZES, DJAY_VIEWBOX, DETAIL_THRESHOLD, ARM_STUB_MIN, MOUTH_MIN } from './types';
import type { DjayAvatarProps } from './types';
import { VARIANT_COLORS } from './palette';
import { ensureAnimationStyles } from './animationStyles';
import { BodyLayers } from './parts/BodyLayers';
import { Accessory } from './parts/Accessory';
import { Treatment } from './parts/Treatment';
export function DjayAvatar({
  size = 'md',
  emotion = 'happy',
  tier = 'rookie',
  variant = 'default',
  accessory = 'none',
  treatment = 'none',
  className = '',
  animate = true,
  hideBody = false
}: DjayAvatarProps) {
  const pixelSize = SIZES[size];
  const showDetails = pixelSize >= DETAIL_THRESHOLD;
  const showArmStubs = pixelSize >= ARM_STUB_MIN && pixelSize < DETAIL_THRESHOLD;
  const showFullArms = pixelSize >= DETAIL_THRESHOLD;
  const showTail = pixelSize >= DETAIL_THRESHOLD;
  const showMouth = pixelSize >= MOUTH_MIN;
  // If hideBody is true, force showAccessory regardless of size so we can
  // render just the accessory.
  const showAccessory = (pixelSize >= DETAIL_THRESHOLD || hideBody) && accessory !== 'none';
  const uidRef = useRef(`djay-${size}-${variant}-${Math.random().toString(36).slice(2, 6)}`);
  const uid = uidRef.current;
  const colors = VARIANT_COLORS[variant] ?? VARIANT_COLORS.default;
  const hasTreatment = treatment !== 'none' && !hideBody;
  // Glowing eyes accompany the lit finishes.
  const eyeGlow = treatment === 'glow' || treatment === 'gummy' || treatment === 'aura';
  // Inject animation styles once (singleton)
  if (animate) ensureAnimationStyles();
  return <div className={`relative inline-flex items-center justify-center ${className}`} data-id="element-3240" data-mp="15ruovd34g2om">
      
      <svg width={pixelSize} height={pixelSize} viewBox={DJAY_VIEWBOX} fill="none" xmlns="http://www.w3.org/2000/svg" className={`djay-avatar ${animate ? 'djay-animated' : ''}`} style={{
      overflow: 'visible'
    }} role="img" aria-label={`Djay mascotte (${emotion})`} data-id="element-3241" data-mp="1jqucchewebj7">
        
        <defs data-id="element-3242" data-mp="3hgvnqpuvfe1s">
          {variant === 'rainbow' ? <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3243" data-mp="3hhxcty8q212i">
            
              <stop offset="0%" stopColor="#F87171" data-id="element-3244" data-mp="37dqmysgaer4e" />
              <stop offset="20%" stopColor="#FB923C" data-id="element-3245" data-mp="3bese0m01vwva" />
              <stop offset="40%" stopColor="#FBBF24" data-id="element-3246" data-mp="3bgmy5y3hzkyb" />
              <stop offset="60%" stopColor="#34D399" data-id="element-3247" data-mp="2xm2s0hkiury6" />
              <stop offset="80%" stopColor="#60A5FA" data-id="element-3248" data-mp="2xlb3trt97whx" />
              <stop offset="100%" stopColor="#A78BFA" data-id="element-3249" data-mp="32kxim5oqdksu" />
            </linearGradient> : <linearGradient id={`${uid}-body-grad`} x1="35" y1="45" x2="165" y2="195" gradientUnits="userSpaceOnUse" data-id="element-3250" data-mp="2nog6vdcmq45r">
            
              <stop offset="0%" stopColor={colors.bodyGradient[0]} data-id="element-3251" data-mp="2spm4npl5chm5" />
              <stop offset="45%" stopColor={colors.bodyGradient[1]} data-id="element-3252" data-mp="2ep1l2r7eqhrl" />
              <stop offset="100%" stopColor={colors.bodyGradient[2]} data-id="element-3253" data-mp="2jncuj0tsiq9a" />
            </linearGradient>}
          {/* Soft top-left sheen — gives the body a rounded, lit-from-above
            read. Palette-agnostic (white→transparent), so it layers
            correctly over every variant without a per-variant entry. */}
          <radialGradient id={`${uid}-body-sheen`} cx="38%" cy="32%" r="60%" gradientUnits="objectBoundingBox" data-id="element-3254" data-mp="2dzo0xdo57x3x">
            
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.55" data-id="element-3255" data-mp="2912w10xy4p95" />
            <stop offset="45%" stopColor="#FFFFFF" stopOpacity="0.12" data-id="element-3256" data-mp="240f9argr2ukq" />
            <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" data-id="element-3257" data-mp="242t4d0bfk6sa" />
          </radialGradient>
          {/* Bottom inner shade — deepens the lower belly so the form has
            volume rather than reading flat. Uses the darkest gradient stop. */}
          <radialGradient id={`${uid}-body-shade`} cx="55%" cy="92%" r="62%" gradientUnits="objectBoundingBox" data-id="element-3258" data-mp="1q8ij4syn6uqz">
            
            <stop offset="0%" stopColor={colors.bodyGradient[2]} stopOpacity="0.55" data-id="element-3259" data-mp="1laqgb3wglo80" />
            
            <stop offset="60%" stopColor={colors.bodyGradient[2]} stopOpacity="0.14" data-id="element-3260" data-mp="1gd7387bhzhhn" />
            
            <stop offset="100%" stopColor={colors.bodyGradient[2]} stopOpacity="0" data-id="element-3261" data-mp="1c8zc63hkhpg9" />
            
          </radialGradient>
          {hasTreatment && <>
              <radialGradient id={`${uid}-treatment-radial`} cx="50%" cy="55%" r="55%" data-id="element-3262" data-mp="3iyuab81es43n">
              
                <stop offset="0%" stopColor={colors.treatmentAccent} stopOpacity="0.65" data-id="element-3263" data-mp="3nwckt2v2qrdr" />
              
                <stop offset="55%" stopColor={colors.treatmentAccent} stopOpacity="0.22" data-id="element-3264" data-mp="3rzsk66yj35u5" />
              
                <stop offset="100%" stopColor={colors.treatmentAccent} stopOpacity="0" data-id="element-3265" data-mp="r4wvlgq98jy" />
              
              </radialGradient>
              <filter id={`${uid}-treatment-soft`} x="-30%" y="-30%" width="160%" height="160%" data-id="element-3266" data-mp="305gq9xz8hi2t">
              
                <feGaussianBlur stdDeviation="4" data-id="element-3267" data-mp="39882w13uijw1" />
              </filter>
            </>}
        </defs>
        {hasTreatment && <g className="djay-treatment-under" data-id="element-3268" data-mp="3dyoj1sz1wjm3">
            <Treatment treatment={treatment} layer="under" uid={uid} colors={colors} showDetails={showDetails} data-id="element-3269" data-mp="3e0j55am26hrr" />
          
          </g>}
        {!hideBody && <BodyLayers uid={uid} colors={colors} emotion={emotion} tier={tier} variant={variant} showTail={showTail} showDetails={showDetails} showMouth={showMouth} showFullArms={showFullArms} showArmStubs={showArmStubs} eyeGlow={eyeGlow} data-id="element-3270" data-mp="2lido3dzyul95" />}
        {hasTreatment && <g className="djay-treatment-over" data-id="element-3271" data-mp="3ju7z060qv539">
            <Treatment treatment={treatment} layer="over" uid={uid} colors={colors} showDetails={showDetails} data-id="element-3272" data-mp="1h0a8eun8b4z" />
          
          </g>}
        <g className="djay-accessory-float" data-id="element-3273" data-mp="3tkt4mdeu59vo">
          <Accessory accessory={accessory} uid={uid} showAccessory={showAccessory} data-id="element-3274" data-mp="35up3ccp4dm7u" />
          
        </g>
      </svg>
    </div>;
}